import { useState, useCallback, useEffect } from "react";
import apiClient2 from "../../api/apiClient2";
import apiPaths from "../../api/endpoints";

export interface BreatheEmoji {
  image: string;
  linkTo: string;
}

export interface BreatheExercise {
  title: string;
  images: { url: string }[];
  audio?: string;
  bgMusic?: string;
  duration: string;
  showUntil: number | null;
}

export interface MindfulMoment {
  title: string;
  audioUrl: string;
  bgMusicUrl: string;
}

export interface BreatheContent {
  label: string;
  exercises: BreatheExercise[];
  userAction: {
    title: string;
    emojis: BreatheEmoji[];
  };
  bottomImage: string;
  guidedMeditation?: string;
  mindfulMoment?: MindfulMoment;
}

interface Options {
  isDance?: boolean;
  isMusic?: boolean;
}

export function useRetrieveBreatheExercise({ isDance, isMusic }: Options = {}) {
  const [data, setData] = useState<BreatheContent | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchContent = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const path = isDance
        ? apiPaths.FETCH_BREATHE_LETS_DANCE
        : isMusic
        ? apiPaths.FETCH_BREATHE_CALM_MUSIC
        : apiPaths.FETCH_BREATHE_MODULE;
      const res = await apiClient2.get(path);
      setData(res.data?.data ?? res.data);
    } catch (err: any) {
      console.error("useRetrieveBreatheExercise error:", err);
      setError(err?.response?.data?.message || err?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [isDance, isMusic]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  return { data, loading, error };
}

export const useRetrievePostContent = () => {
  const postRetrieveContent = useCallback(async (data:any) => {
    try {
      const response = await apiClient2.post(
        `${apiPaths.CAPTURE_CONTENT_RESPONSE}`,
        data,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );
      return response.data;
    } catch (error) {
      return error;
    }
  }, []);

  return {
    postRetrieveContent,
  };
};

// ── Grown-up (Talk to Someone) ──
export interface GrownupAction {
  buttonText: string;
  linkTo?: string;
}

export interface GrownupBlock {
  screenNo: number;
  topText?: string;
  bottomText?: string;
  topTextAudio?: string;
  bottomTextAudio?: string;
  character1?: string;
  character2?: string;
  userActions?: GrownupAction[];
}

export interface GrownupContent {
  title: string;
  blocks: GrownupBlock[];
}

export function useRetrieveBreatheGrownup() {
  const [data, setData] = useState<GrownupContent | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchContent = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiClient2.get(apiPaths.FETCH_TALK_TO_SOMEONE);
      setData(res.data?.data ?? res.data);
    } catch (err: any) {
      console.error("useRetrieveBreatheGrownup error:", err);
      setError(err?.response?.data?.message || err?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  return { data, loading, error };
}
